import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useImperativeHandle,
  forwardRef,
  lazy,
  Suspense,
} from "react";
import PropTypes from "prop-types";
import { ContextMenu } from "primereact/contextmenu";
import { useChartsStore } from "../../../store/chartStore";
import useWorkspaceStore from "../../../store/workspaceStore";
import { symbolSpotlightRef } from "../../Navbar/symbolSpotlightRef";
import { activeIndicatorsModalRef } from "../../Modals/ActiveIndicatorsModalRef";
import { indicatorsPickerRef } from "../../Navbar/indicatorsPickerRef";
import { barColorModalRef } from "../../Navbar/barColorModalRef";
import { workspaceTemplateRef } from "../../Footer/workspaceTemplateRef";
import getChartHostBody from "~/utils/chartHostBody";
import { buildChartContextMenuModel } from "./chartContextMenuModel.js";
import {
  getNativeEvent,
  normalizeShowEvent,
  mergeContextPayload,
  clampMenuToViewport,
} from "./chartContextMenuEvent.js";
import "./ChartContextMenu.css";

// Lazy — the modal is already lazy-loaded elsewhere (objects sidebar), so
// this adds no new startup weight.
const ActiveObjectsModal = lazy(
  () => import("../../Modals/ActiveObjectsModal"),
);

const EMPTY_MODEL = [];

/**
 * Unified chart context menu (feature flag `chartContextMenu`).
 *
 * One right-click / long-press on the chart broadcasts to several subscribers
 * of the same ChartCanvas event (background <Chart>, DrawingObjectSelector,
 * hovered indicator series). Each source notifies this component; payloads for
 * the SAME native event are merged (chartContextMenuEvent.js) so a single menu
 * opens with every applicable section:
 *   - "Gráfico" (always): navbar controls — Símbolo, Período, Estudos, Tipo de
 *     gráfico, Cores, Estudos/Objetos Ativos. Actions reuse the exact same
 *     stores/flows as the navbar (nothing moves out of the navbar); "Estudos…"
 *     and "Cores…" open the SAME modals the navbar uses (picker/BarColorModal)
 *     via their global refs.
 *   - "Desenho" / indicator sections (when clicked on one): delegate to the
 *     existing InteractiveContextMenu / IndicatorContextMenu imperative APIs.
 *
 * Show is deferred one commit so the ContextMenu portal can re-target appendTo
 * to the clicked window's body before positioning (popout, #375) — same
 * mechanism as the two existing menus.
 */
const ChartContextMenu = forwardRef(function ChartContextMenu(
  {
    chartId,
    interactiveMenuRef,
    indicatorMenuRef,
    onDeleteInteractive,
    mainInteractiveApi,
  },
  ref,
) {
  const cmRef = useRef(null);
  const activeObjectsRef = useRef(null);
  // Pending notification for the current native event (merged across sources)
  const pendingRef = useRef(null);
  const [pendingSeq, setPendingSeq] = useState(0);
  // Committed menu request: { seq, context, body, showEvent }
  const [menuState, setMenuState] = useState(null);

  const notify = useCallback((payload, event) => {
    const nativeEvent = getNativeEvent(event);
    const { pending, isNewEvent } = mergeContextPayload(
      pendingRef.current,
      nativeEvent,
      payload,
    );
    pendingRef.current = pending;
    if (isNewEvent) {
      // Suppress the browser menu NOW (synchronously with the event);
      // the actual show is deferred one commit for the appendTo re-target.
      nativeEvent?.preventDefault?.();
      setPendingSeq((seq) => seq + 1);
    }
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      // Right-click / long-press on empty chart area (background <Chart>)
      showChartMenu: (moreProps, event) => {
        notify({ moreProps }, event);
      },
      // Same event landed on a drawing (DrawingObjectSelector path)
      showWithInteractive: (item, moreProps, event) => {
        notify({ interactive: { item, moreProps }, moreProps }, event);
      },
      // Same event landed on an indicator series
      showWithIndicator: (indicator, moreProps, event) => {
        notify({ indicator, moreProps }, event);
      },
    }),
    [notify],
  );

  // Commit the pending request: resolve the target window/document, normalize
  // coordinates (mouse, touch, or synthetic long-press) and focus the clicked
  // chart so the shared modals (spotlight, estudos) act on it.
  useEffect(() => {
    if (pendingSeq === 0 || !pendingRef.current) return;
    const pending = pendingRef.current;
    const nativeEvent = pending.nativeEvent;

    const targetDoc =
      nativeEvent?.target?.ownerDocument ??
      getChartHostBody(chartId)?.ownerDocument ??
      document;
    const container =
      targetDoc.querySelector(
        `.chart-panel[data-chart-id="${CSS.escape(String(chartId))}"] .react-stockcharts`,
      ) ?? targetDoc.querySelector(".react-stockcharts");

    const showEvent = normalizeShowEvent(
      nativeEvent,
      pending.moreProps,
      targetDoc,
      container,
    );
    if (!showEvent) return;

    const chartsState = useChartsStore.getState();
    if (chartsState.focusedChartId !== chartId) {
      chartsState.setFocusedChart(chartId);
    }
    const workspaceState = useWorkspaceStore.getState();
    if (workspaceState.focusedPanelType !== "chart") {
      workspaceState.setFocusedPanelType("chart");
    }

    setMenuState({
      seq: pendingSeq,
      context: {
        interactive: pending.interactive,
        indicator: pending.indicator,
      },
      body: targetDoc.body,
      showEvent,
    });
  }, [pendingSeq, chartId]);

  // Show only after the render that committed the model + appendTo target.
  useEffect(() => {
    if (!menuState) return;
    cmRef.current?.show(menuState.showEvent);
  }, [menuState]);

  // ── Handlers: every action reuses the navbar/store flows ─────────────────
  const handleSelectSymbol = useCallback(() => {
    symbolSpotlightRef.current?.open();
  }, []);

  const handleSelectPeriodicity = useCallback(
    (value) => {
      const { updateChartConfig, fetchChartData } = useChartsStore.getState();
      updateChartConfig(chartId, { periodicity: value, xExtents: null });
      fetchChartData(chartId);
    },
    [chartId],
  );

  const handleSelectChartType = useCallback(
    (value) => {
      useChartsStore.getState().updateChartConfig(chartId, {
        chartType: value,
      });
    },
    [chartId],
  );

  // "Cores…" / "Estudos…" open the SHARED surfaces (BarColorModal / navbar
  // picker). The menu-open effect already focused the clicked chart, so both
  // modals' getSelectedChart() resolves to it.
  const handleOpenBarColors = useCallback(() => {
    barColorModalRef.current?.open();
  }, []);

  const handleOpenIndicatorsPicker = useCallback(() => {
    indicatorsPickerRef.current?.open();
  }, []);

  // "Template ▸" — triggers the footer WorkspaceMenus dialogs via its ref.
  const handleTemplateAction = useCallback((action) => {
    workspaceTemplateRef.current?.[action]?.();
  }, []);

  const handleEditInteractive = useCallback(
    (interactive) => {
      interactiveMenuRef?.current?.openEditFor(
        interactive.item,
        interactive.moreProps,
      );
    },
    [interactiveMenuRef],
  );

  const handleDeleteInteractive = useCallback(
    (interactive) => {
      if (typeof onDeleteInteractive === "function") {
        onDeleteInteractive(chartId, {
          ...interactive.item,
          moreProps: interactive.moreProps,
        });
      }
    },
    [onDeleteInteractive, chartId],
  );

  const handleEditIndicator = useCallback(
    (indicator) => {
      indicatorMenuRef?.current?.edit(indicator);
    },
    [indicatorMenuRef],
  );

  const handleRemoveIndicator = useCallback(
    (indicator) => {
      indicatorMenuRef?.current?.remove(indicator);
    },
    [indicatorMenuRef],
  );

  const handleOpenActiveIndicators = useCallback(() => {
    activeIndicatorsModalRef.current?.open();
  }, []);

  const handleOpenActiveObjects = useCallback(() => {
    activeObjectsRef.current?.open();
  }, []);

  const handlers = useMemo(
    () => ({
      onSelectSymbol: handleSelectSymbol,
      onSelectPeriodicity: handleSelectPeriodicity,
      onSelectChartType: handleSelectChartType,
      onOpenBarColors: handleOpenBarColors,
      onOpenIndicatorsPicker: handleOpenIndicatorsPicker,
      onTemplateAction: handleTemplateAction,
      onEditInteractive: handleEditInteractive,
      onDeleteInteractive: handleDeleteInteractive,
      onEditIndicator: handleEditIndicator,
      onRemoveIndicator: handleRemoveIndicator,
      onOpenActiveIndicators: handleOpenActiveIndicators,
      onOpenActiveObjects: handleOpenActiveObjects,
    }),
    [
      handleSelectSymbol,
      handleSelectPeriodicity,
      handleSelectChartType,
      handleOpenBarColors,
      handleOpenIndicatorsPicker,
      handleTemplateAction,
      handleEditInteractive,
      handleDeleteInteractive,
      handleEditIndicator,
      handleRemoveIndicator,
      handleOpenActiveIndicators,
      handleOpenActiveObjects,
    ],
  );

  // Model is rebuilt only when a menu request commits (open) — chart config is
  // read from the store at build time (no broad subscription → no re-render
  // coupling with quote ticks).
  const model = useMemo(() => {
    if (!menuState) return EMPTY_MODEL;
    const chart = useChartsStore.getState().getChart(chartId);
    return buildChartContextMenuModel({
      chart,
      context: menuState.context,
      handlers,
    });
  }, [menuState, chartId, handlers]);

  return (
    <>
      <ContextMenu
        model={model}
        ref={cmRef}
        appendTo={menuState?.body ?? document.body}
        baseZIndex={100000}
        className="chart-context-menu"
        onShow={() =>
          requestAnimationFrame(() =>
            clampMenuToViewport(cmRef.current?.getElement?.()),
          )
        }
      />
      <Suspense fallback={<div></div>}>
        <ActiveObjectsModal
          ref={activeObjectsRef}
          interactiveApi={mainInteractiveApi}
          hideTrigger
        />
      </Suspense>
    </>
  );
});

ChartContextMenu.propTypes = {
  chartId: PropTypes.string.isRequired,
  interactiveMenuRef: PropTypes.shape({ current: PropTypes.any }),
  indicatorMenuRef: PropTypes.shape({ current: PropTypes.any }),
  onDeleteInteractive: PropTypes.func,
  mainInteractiveApi: PropTypes.object,
};

export default React.memo(ChartContextMenu);
